import {
  Alert,
  Fieldset,
  Spinner,
  EXPERIMENTAL_Suggestion as Suggestion,
  type SuggestionItem,
} from "@digdir/designsystemet-react";
import type { AxiosError } from "axios";
import debounce from "lodash.debounce";
import {
  type InputEvent,
  useCallback,
  useEffect,
  useId,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import "./asyncSuggestion.css";
import { KeyValueItem } from "../KeyValueItem";

export interface AsyncSuggestionOption extends SuggestionItem {
  extraLabels?: { label: string; value: string }[];
}

export const AsyncSuggestions = <T extends AsyncSuggestionOption>({
  title,
  description,
  onSelectedChanged,
  getOptions,
  debounceMs = 500,
}: {
  title: string;
  description?: string;
  onSelectedChanged: (selected: T | undefined) => void;
  getOptions: (query: string) => Promise<{ data?: T[]; error?: AxiosError }>;
  debounceMs?: number;
}) => {
  const { t } = useTranslation();

  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState<T | undefined>(undefined);
  const [options, setOptions] = useState<T[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isErrorLoadingOptions, setIsErrorLoadingOptions] = useState(false);
  const selectId = useId();

  const handleSearch = async (value: string): Promise<void> => {
    if (value.length < 1) {
      setOptions([]);
      setIsLoadingOptions(false);
      return;
    }

    setIsLoadingOptions(true);
    setIsErrorLoadingOptions(false);
    const results = await getOptions(value);

    if (results.data != null) {
      setOptions(results.data);
    } else {
      setIsErrorLoadingOptions(true);
      console.error(results.error);
    }
    setIsLoadingOptions(false);
  };

  const debouncedSearch = useCallback(debounce(handleSearch, debounceMs), []);

  useEffect(() => {
    setIsLoadingOptions(true);
    debouncedSearch(searchInput);

    return () => {
      debouncedSearch.cancel();
    };
  }, [searchInput, debouncedSearch]);

  const handleInput = (event: InputEvent<HTMLInputElement>) => {
    const inputValue = event.currentTarget.value;

    // Determine if input value was set as a result of selecting an option,
    // in which case the input value will be set to the option's label
    const inputSetBySelectedOption = options.some(
      (s) => s.label === inputValue,
    );

    // Only call setSearchInput if the user is searching for data
    // If inputSetBySelectedOption, the user is not querying/searching but has made a selection
    if (!inputSetBySelectedOption) {
      setSearchInput(inputValue);
      onSelectedChanged(undefined);
    }
  };

  const handleOnSelectedChanged = (e: T | undefined) => {
    setSelected(e);
    onSelectedChanged(e);
  };

  // Set filter to false since we are assuming `options` are already filtered on the input
  // (Otherwise default filter function is applied to options)
  const filter = false;

  return (
    <div className="async-suggestion-root">
      <Fieldset>
        {title && <Fieldset.Legend>{title}</Fieldset.Legend>}
        {description && (
          <Fieldset.Description>{description}</Fieldset.Description>
        )}
        <Suggestion selected={selected} filter={filter} id={selectId}>
          <Suggestion.Input
            aria-label={t("components.async-suggestions.søk")}
            onInput={handleInput}
          />
          <Suggestion.Clear />
          <Suggestion.List className="h-fit max-h-[500px] space-y-4">
            {isLoadingOptions ? (
              <Suggestion.Empty>
                <div className="text-center py-2 px-6">
                  <Spinner
                    data-size="sm"
                    aria-label={t(
                      "components.async-suggestions.laster-aria-label",
                    )}
                  />
                </div>
              </Suggestion.Empty>
            ) : options.length < 1 ? (
              <Suggestion.Empty>
                <div className="text-center py-2 px-6">
                  {!searchInput
                    ? t("components.async-suggestions.ingen-streng")
                    : t("components.async-suggestions.ingen-resultat")}
                </div>
              </Suggestion.Empty>
            ) : (
              options.map((s: T) => (
                <Suggestion.Option
                  key={s.value}
                  value={s.value}
                  label={s.label}
                  onClick={() => handleOnSelectedChanged(s)}
                >
                  <div className="px-6 py-3 w-full cursor-pointer hover:bg-balder-background-tinted border-b border-b-balder-surface-tinted">
                    <span className="font-bold">{s.label}</span>
                    {s.extraLabels != null && (
                      <div className="flex flex-col">
                        {s.extraLabels?.map((l) => (
                          <KeyValueItem
                            key={l.value}
                            label={l.label}
                            value={l.value}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </Suggestion.Option>
              ))
            )}
          </Suggestion.List>
        </Suggestion>
        {isErrorLoadingOptions && (
          <Alert data-color="danger" className="mt-4">
            {t("generisk.feil")}
          </Alert>
        )}
      </Fieldset>
    </div>
  );
};
